import React, { useState, useEffect, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { CURRENCY_LIST } from '../constants';
import { checkDiscountAvailability } from '../constants/commonFunc';

const INITIAL_DELAY = 2000;
const DEFAULT_DISPLAY_DURATION = 5000;
const EXIT_DURATION = 500;
const LOOP_GAP = 1000;

const currencyMap = Object.fromEntries(
  CURRENCY_LIST.map(c => [c.currency.toLowerCase(), c.symbol])
);

const getMinPurchase = (discount) => {
  // fixed
  if (discount.minPurchase) {
    return discount.minPurchase;
  }

  // tiered → lowest minPurchase
  if (discount.tiers?.length) {
    return discount.tiers[0].minPurchase;
  }

  return null;
};

export const mapDiscountsToBanners = (rows, bannerImage) => {
  return rows
    .filter(checkDiscountAvailability)
    .map((d) => {
      const minPurchase = getMinPurchase(d);

      const subtitle = minPurchase
        ? `On a minimum purchase of ${currencyMap[
        minPurchase.currency.toLowerCase()
        ]}${minPurchase.value}`
        : "";

      return {
        title: d.description || "Discount available",
        subtitle,
        image: bannerImage,
        displayDuration: DEFAULT_DISPLAY_DURATION,
      };
    });
};

/**
 * Build the slides array purely from API data.
 * Returns an empty array when there is nothing available to show, in which
 * case the banner is not rendered at all.
 */
export const buildBannerSlides = (rows, bannerImage) =>
  Array.isArray(rows) ? mapDiscountsToBanners(rows, bannerImage) : [];

/**
 * Animation phases for each slide:
 *   'hidden'  → off-screen right (translateX(100%) scale(1)), no transition
 *   'visible' → on-screen (translateX(0) scale(1)), ease-out transition
 *   'exiting' → shrink to center (translateX(0) scale(0)), cubic-bezier transition
 *
 * Sequence per slide:
 *   hidden → visible → (display duration) → exiting → (exit duration) → hidden
 *   Then advance to next slide index and repeat.
 */

const ConfigurableBannerSlider = ({
  bannerHeight,
  onClick,
  styles,
  slides = [],
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [contentPhase, setContentPhase] = useState("hidden");
  const timersRef = useRef([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const addTimer = useCallback((callback, delay) => {
    const id = setTimeout(callback, delay);
    timersRef.current.push(id);
    return id;
  }, []);

  useEffect(() => {
    const totalSlides = slides.length;
    if (!totalSlides) return;

    let slideIndex = 0;

    const runSlide = (delay) => {
      let t = delay;

      // 1. Slide in from right
      addTimer(() => {
        setCurrentSlideIndex(slideIndex);
        setContentPhase("visible");
      }, t);

      const displayDuration =
        slides[slideIndex]?.displayDuration ?? DEFAULT_DISPLAY_DURATION;
      t += displayDuration;

      // 2. Shrink to center
      addTimer(() => setContentPhase("exiting"), t);
      t += EXIT_DURATION;

      // 3. Snap off-screen (no transition)
      addTimer(() => setContentPhase("hidden"), t);
      t += LOOP_GAP;

      // 4. Advance to next slide and continue
      addTimer(() => {
        slideIndex = (slideIndex + 1) % totalSlides;
        runSlide(0);
      }, t);
    };

    runSlide(INITIAL_DELAY);

    return clearTimers;
  }, [slides.length, addTimer, clearTimers]);

  if (!slides.length) return null;

  const currentSlide = slides[currentSlideIndex];

  return (
    <Box
      sx={{ ...styles.bannerView, height: bannerHeight, cursor: "pointer" }}
      onClick={() => {
        const link = currentSlide?.link;
        if (link) window.open(link, "_blank");
        else if (onClick) onClick();
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          transform:
            contentPhase === "exiting"
              ? "translateX(0) scale(0)"
              : contentPhase === "visible"
                ? "translateX(0) scale(1)"
                : "translateX(100%) scale(1)",
          transition:
            contentPhase === "hidden"
              ? "none"
              : contentPhase === "exiting"
                ? "transform 0.5s cubic-bezier(0.4, 0, 0.6, 1)"
                : "transform 0.5s ease-out",
        }}
      >
        {currentSlide?.image ? (
          <img
            src={currentSlide.image}
            alt={currentSlide.title || "Banner"}
            style={styles.bannerImg}
          />
        ) : (
          <Box sx={styles.bannerFallback} />
        )}

        <Box sx={styles.bannerTextOverlay}>
          {currentSlide?.title && (
            <Typography sx={styles.bannerTitle}>
              {currentSlide.title}
            </Typography>
          )}
          {currentSlide?.subtitle && (
            <Typography sx={styles.bannerSubtitle}>
              {currentSlide.subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(ConfigurableBannerSlider);
